import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lightning, GoogleLogo, Eye, EyeSlash, CheckCircle } from '@phosphor-icons/react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      let msg = err.response?.data?.detail;
      if (Array.isArray(msg)) msg = msg.map(m => m.msg).join(' ');
      setError(msg || "Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden py-12 px-4">
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full opacity-20 mix-blend-screen bg-gradient-to-br from-primary/30 to-background/5" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent z-0" />
      
      <div className="z-10 w-full max-w-5xl flex flex-col md:flex-row gap-8 items-center justify-center">
        
        {/* Value Proposition Section */}
        <div className="md:w-1/2 space-y-8 p-4">
          <div className="space-y-4">
            <h1 className="text-5xl font-heading text-white tracking-widest uppercase">Dominate Your Exams</h1>
            <p className="text-muted-foreground font-bold tracking-widest uppercase text-sm">Join the elite platform designed for top-tier competitors.</p>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <CheckCircle size={24} weight="fill" className="text-primary mt-1 shrink-0" />
              <div>
                <h3 className="font-heading text-xl text-white uppercase tracking-widest">AI-Powered Mock Tests</h3>
                <p className="text-sm text-muted-foreground">Generate infinite, customized 10-question mock exams for JEE, NEET, IAS, and CAT dynamically.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <CheckCircle size={24} weight="fill" className="text-chart-4 mt-1 shrink-0" />
              <div>
                <h3 className="font-heading text-xl text-white uppercase tracking-widest">All India Leaderboards</h3>
                <p className="text-sm text-muted-foreground">Compare your accuracy and speed against students across the nation and within your specific college.</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <CheckCircle size={24} weight="fill" className="text-chart-5 mt-1 shrink-0" />
              <div>
                <h3 className="font-heading text-xl text-white uppercase tracking-widest">Personal AI Coach</h3>
                <p className="text-sm text-muted-foreground">Get automated deep-dive analytics on your weak areas and personalized study strategies tailored to your exact syllabus struggles.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="w-full md:w-1/2 max-w-md rounded-none border-white/10 bg-black/60 backdrop-blur-xl">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto bg-primary/20 p-4 rounded-full w-20 h-20 flex items-center justify-center border border-primary/50">
              <Lightning size={40} weight="duotone" className="text-primary" />
            </div>
            <CardTitle className="font-heading text-4xl">Mockrankai Login</CardTitle>
            <CardDescription className="text-muted-foreground uppercase tracking-widest font-bold text-xs">Enter your credentials to access the command center</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && <div className="text-destructive text-sm font-bold bg-destructive/10 p-3 border border-destructive/20" data-testid="login-error">{error}</div>}
              <div className="space-y-4">
                <Input type="email" placeholder="ENTER EMAIL" value={email} onChange={e => setEmail(e.target.value)} required className="h-12 bg-black/50 border-white/10 rounded-none text-white tracking-wider" data-testid="login-email" />
                
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    placeholder="ENTER PASSWORD" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    className="h-12 bg-black/50 border-white/10 rounded-none text-white tracking-wider pr-10" 
                    data-testid="login-password" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full h-14 font-heading text-xl tracking-wider rounded-none bg-primary text-white hover:bg-primary/80" data-testid="login-submit">
                INITIALIZE LOGIN
              </Button>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-black/60 px-2 text-muted-foreground font-bold tracking-widest">OR CONTINUE WITH</span>
              </div>
            </div>
            
            <Button type="button" onClick={handleGoogleLogin} variant="outline" className="w-full h-14 font-heading text-xl tracking-wider rounded-none border-border hover:bg-secondary flex items-center justify-center gap-2">
              <GoogleLogo size={24} weight="bold" /> GOOGLE
            </Button>

            <div className="flex flex-col space-y-4 mt-6 text-center">
              <div>
                <span className="text-muted-foreground text-sm tracking-widest">FORGOT PASSWORD? </span>
                <Link to="/forgot-password" className="text-chart-4 text-sm font-bold tracking-widest hover:underline">RESET NOW</Link>
              </div>
              <div>
                <span className="text-muted-foreground text-sm tracking-widest">NO ACCOUNT? </span>
                <Link to="/register" className="text-primary text-sm font-bold tracking-widest hover:underline">REGISTER NOW</Link>
              </div>
              <div className="pt-4 border-t border-white/10">
                <Link to="/legal" className="text-xs text-muted-foreground hover:text-white transition-colors tracking-widest uppercase">Terms of Service & Privacy Policy</Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}